import React, { useState, useEffect, useRef } from "react";
import { createRepair } from "../../services/api";
import { getCustomers } from "../../services/customerService";
import { AiOutlineClose, AiOutlinePlus, AiOutlineForm, AiOutlineIdcard, AiOutlineSearch, AiOutlineUser, AiOutlinePhone } from "react-icons/ai";
import styles from "../../styles/Repairs.module.css";
import generateReceiptContent from "../../pages/Repairs/CollectionReceiptTemplate";

const CreateRepairModal = ({ onClose, onRepairCreated }) => {
    const [serialNumber, setSerialNumber] = useState("");
    const [customerNic, setCustomerNic] = useState("");
    const [customerSearchText, setCustomerSearchText] = useState("");
    const [issueDescription, setIssueDescription] = useState("");
    const [isExternal, setIsExternal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const customerDropdownRef = useRef(null);

    useEffect(() => {
        // Load customers when component mounts
        fetchCustomers();

        // Add click event listener to handle outside clicks
        const handleClickOutside = (event) => {
            if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
                setIsCustomerDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Filter customers when search text changes - Based on actual API response
    useEffect(() => {
        if (customers.length > 0 && customerSearchText) {
            const lowerCaseSearch = customerSearchText.toLowerCase();
            const filtered = customers.filter(customer => {
                // Based on the actual API response format
                const nicMatch = customer.NIC && customer.NIC.toString().toLowerCase().includes(lowerCaseSearch);
                const firstNameMatch = customer.FirstName && customer.FirstName.toString().toLowerCase().includes(lowerCaseSearch);
                const lastNameMatch = customer.LastName && customer.LastName.toString().toLowerCase().includes(lowerCaseSearch);
                const phoneMatch = customer.ContactNumber && customer.ContactNumber.toString().toLowerCase().includes(lowerCaseSearch);

                return nicMatch || firstNameMatch || lastNameMatch || phoneMatch;
            });
            setFilteredCustomers(filtered);
        } else {
            // If no search text, show all customers
            setFilteredCustomers(customers);
        }
    }, [customerSearchText, customers]);

    const fetchCustomers = async () => {
        try {
            setIsLoading(true);
            const data = await getCustomers();
            // API returns an array directly as seen in the screenshot
            const customersArray = Array.isArray(data) ? data : [];

            setCustomers(customersArray);
            setFilteredCustomers(customersArray);
            setIsLoading(false);
        } catch (error) {
            console.error("Failed to fetch customers:", error);
            setIsLoading(false);
            setCustomers([]);
            setFilteredCustomers([]);
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!serialNumber.trim()) newErrors.serialNumber = "Serial number is required";
        if (!customerNic.trim()) newErrors.customerNic = "Customer NIC is required";
        if (!issueDescription.trim()) newErrors.issueDescription = "Issue description is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreateRepair = async () => {
        if (!validate()) return;

        try {
            setIsLoading(true);
            const repairData = {
                serialNumber,
                customerNic,
                issueDescription,
                repairDate: new Date().toISOString().split("T")[0],
                isExternal,
            };
            await createRepair(repairData);
            setIsLoading(false);
            onRepairCreated();
            openPrintPreview(repairData); // Trigger receipt print
            onClose();
        } catch (error) {
            setIsLoading(false);
            alert("Error creating repair");
            console.error(error);
        }
    };

    // Function to open the print preview for collection receipt
    const openPrintPreview = (repairData) => {
        const receiptContent = generateReceiptContent({
            date: new Date().toLocaleDateString(),
            customerName: selectedCustomer ? `${selectedCustomer.FirstName} ${selectedCustomer.LastName}` : "Unknown",
            customerNIC: repairData.customerNic,
            serialNumber: repairData.serialNumber,
            issueDescription: repairData.issueDescription,
        });

        const newWindow = window.open();
        newWindow.document.write(receiptContent);
        newWindow.document.close();
        newWindow.print();
    };

    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setCustomerNic(customer.NIC || '');
        setCustomerSearchText(`${customer.FirstName || ''} ${customer.LastName || ''}`.trim());
        setIsCustomerDropdownOpen(false);
    };

    const handleCustomerInputFocus = () => {
        setIsCustomerDropdownOpen(true);
    };

    // Format customer's full name
    const getCustomerFullName = (customer) => {
        return `${customer.FirstName || ''} ${customer.LastName || ''}`.trim() || 'Unknown';
    };

    return (
        <div className={styles["repair-modal-overlay"]}>
            <div className={styles["create-repair-modal"]}>
                <div className={styles["create-repair-header"]}>
                    <h3><AiOutlineForm className={styles["header-icon"]} /> Create Repair</h3>
                    <button onClick={onClose} className={styles["modal-close-btn"]}>
                        <AiOutlineClose />
                    </button>
                </div>

                <div className={styles["create-repair-content"]}>
                    <div className={styles["form-group"]}>
                        <label htmlFor="serial-number" className={styles["form-label"]}>Serial Number</label>
                        <div className={styles["input-with-icon"]}>
                            <AiOutlineIdcard className={styles["input-icon"]} />
                            <input
                                id="serial-number"
                                type="text"
                                value={serialNumber}
                                onChange={(e) => setSerialNumber(e.target.value)}
                                className={`${styles["form-control"]} ${errors.serialNumber ? styles["form-control-error"] : ""}`}
                                placeholder="Enter serial number"
                            />
                        </div>
                        {errors.serialNumber && <div className={styles["error-message"]}>{errors.serialNumber}</div>}
                    </div>

                    <div className={styles["form-group"]} ref={customerDropdownRef}>
                        <label htmlFor="customer-search" className={styles["form-label"]}>Customer</label>
                        <div className={styles["input-with-icon"]}>
                            <AiOutlineUser className={styles["input-icon"]} />
                            <input
                                id="customer-search"
                                type="text"
                                value={customerSearchText}
                                onChange={(e) => setCustomerSearchText(e.target.value)}
                                onFocus={handleCustomerInputFocus}
                                className={`${styles["form-control"]} ${errors.customerNic ? styles["form-control-error"] : ""}`}
                                placeholder="Search for customer by name or NIC"
                                autoComplete="off"
                            />
                            <AiOutlineSearch className={styles["search-icon"]} />
                        </div>
                        {errors.customerNic && <div className={styles["error-message"]}>{errors.customerNic}</div>}

                        {isCustomerDropdownOpen && (
                            <div className={styles["dropdown-container"]}>
                                {isLoading ? (
                                    <div className={styles["dropdown-loading"]}>
                                        <div className={styles["loading-placeholder"]}></div>
                                        <div className={styles["loading-placeholder"]}></div>
                                    </div>
                                ) : filteredCustomers.length > 0 ? (
                                    filteredCustomers.map((customer, index) => (
                                        <div
                                            key={customer.NIC || index}
                                            className={styles["dropdown-item"]}
                                            onClick={() => handleSelectCustomer(customer)}
                                        >
                                            <div className={styles["dropdown-item-name"]}>
                                                {getCustomerFullName(customer)}
                                            </div>
                                            <div className={styles["dropdown-item-details"]}>
                                                <span className={styles["dropdown-item-nic"]}>
                                                    <span role="img" aria-label="ID Card">📇</span> {customer.NIC}
                                                </span>
                                                {customer.ContactNumber && (
                                                    <span className={styles["dropdown-item-phone"]}>
                                                        <span role="img" aria-label="Phone">📱</span> {customer.ContactNumber}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className={styles["dropdown-no-results"]}>
                                        No customers found
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles["form-group"]}>
                        <label htmlFor="issue-description" className={styles["form-label"]}>Issue Description</label>
                        <textarea
                            id="issue-description"
                            value={issueDescription}
                            onChange={(e) => setIssueDescription(e.target.value)}
                            className={`${styles["form-control"]} ${styles["textarea-control"]} ${errors.issueDescription ? styles["form-control-error"] : ""}`}
                            placeholder="Describe the issue..."
                            rows="4"
                        ></textarea>
                        {errors.issueDescription && <div className={styles["error-message"]}>{errors.issueDescription}</div>}
                    </div>

                    <div className={styles["checkbox-wrapper"]}>
                        <label className={styles["checkbox-container"]}>
                            <input
                                type="checkbox"
                                checked={isExternal}
                                onChange={(e) => setIsExternal(e.target.checked)}
                                className={styles["checkbox-input"]}
                            />
                            <span className={styles["checkmark"]}></span> {/* Checkmark added here */}
                            <span className={styles["checkbox-label"]}>External Repair</span>
                            <span className={styles["checkbox-info"]}>
            Check this if the repair will be done by an outside vendor
        </span>
                        </label>
                    </div>

                </div>

                <div className={styles["create-repair-footer"]}>
                    <button
                        onClick={onClose}
                        className={styles["cancel-button"]}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCreateRepair}
                        className={styles["create-button"]}
                        disabled={isLoading}
                    >
                        {isLoading ? "Creating..." : (
                            <>
                                <AiOutlinePlus /> Create Repair
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateRepairModal;